import {
  ACCOUNT_MISCONFIGURED_PATH,
  canAccessPath,
  getDefaultAuthenticatedPath,
  isAccountMisconfigured,
} from "../modules/appModuleAccess";
import { stripTrailingSlashes } from "../utils/pathUtils";
import { logReturnUrlFlow } from "./returnUrlDebug";

export const INTENDED_RETURN_URL_KEY = "intendedReturnUrl";

const MAX_RETURN_URL_LENGTH = 2048;
const SENSITIVE_QUERY_KEYS = new Set([
  "access_token",
  "jwt",
  "password",
  "session",
  "sessionid",
  "token",
]);

const DENIED_PATH_PREFIXES = [
  "/login",
  "/terms-and-conditions",
  "/external",
  "/required/documents",
  "/supplier/required/documents",
];

const UNSAFE_URL_CHARS = new Set(["<", ">", "\"", "'", "`"]);
const SAFE_RETURN_URL_PATTERN = /^[A-Za-z0-9\-._~/?#[\]@!$&()*+,;=:%]*$/;
const SCHEME_PATTERN = /^[A-Za-z][A-Za-z0-9+.-]*:/;

function hasUnsafeUrlChar(value) {
  return Array.from(value).some((char) => {
    const charCode = char.codePointAt(0);
    return charCode <= 31 || charCode === 127 || UNSAFE_URL_CHARS.has(char);
  });
}

function readSessionStorage(key) {
  try {
    if (!globalThis.window) return null;
    return globalThis.window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key, value) {
  try {
    if (!globalThis.window) return false;
    globalThis.window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeSessionStorage(key) {
  try {
    if (!globalThis.window) return;
    globalThis.window.sessionStorage.removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
}

function decodeForValidation(value) {
  let decoded = value;

  for (let i = 0; i < 3; i += 1) {
    const next = decodeURIComponent(decoded);
    if (next === decoded) return decoded;
    decoded = next;
  }

  return decoded;
}

function splitRelativeUrl(value) {
  const hashIndex = value.indexOf("#");
  const beforeHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const searchIndex = beforeHash.indexOf("?");

  if (searchIndex < 0) {
    return {
      pathname: beforeHash,
      search: "",
      hash,
    };
  }

  return {
    pathname: beforeHash.slice(0, searchIndex),
    search: beforeHash.slice(searchIndex),
    hash,
  };
}

function normalizePathname(pathname) {
  if (!pathname) return "/";
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function hasDeniedPrefix(pathname) {
  const normalizedPathname = stripTrailingSlashes(normalizePathname(pathname));

  return DENIED_PATH_PREFIXES.some((prefix) => (
    normalizedPathname === prefix || normalizedPathname.startsWith(`${prefix}/`)
  ));
}

function hasTraversalSegment(pathname) {
  return pathname
    .split("/")
    .some((segment) => segment === "..");
}

function sanitizeSearch(search) {
  if (!search) return "";

  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const sanitized = new URLSearchParams();

  params.forEach((value, key) => {
    if (!SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
      sanitized.append(key, value);
    }
  });

  const serialized = sanitized.toString();
  return serialized ? `?${serialized}` : "";
}

function invalid(reason) {
  return {
    ok: false,
    normalized: null,
    pathname: null,
    reason,
  };
}

function validateRelativeUrlShape(value, { decoded = false } = {}) {
  const prefix = decoded ? "decoded_" : "";

  if (hasUnsafeUrlChar(value)) return invalid(`${prefix}unsafe_char`);
  if (value.startsWith("//")) return invalid(`${prefix}protocol_relative`);
  if (value.startsWith("\\\\") || value.includes("\\")) return invalid(`${prefix}backslash`);
  if (!SAFE_RETURN_URL_PATTERN.test(value)) return invalid(`${prefix}unsupported_char`);
  if (value.includes("://")) return invalid(`${prefix}absolute_url`);
  if (SCHEME_PATTERN.test(value)) return invalid(`${prefix}scheme`);
  if (!value.startsWith("/")) return invalid(`${prefix}not_relative_path`);

  return null;
}

/**
 * Validate and normalize an app-local post-login return URL.
 *
 * This intentionally accepts only same-origin relative URLs. It rejects
 * absolute/protocol-relative targets, encoded redirect bypasses, traversal,
 * public auth/external flows, and characters that could become executable
 * markup when reflected by a future caller.
 *
 * @param {unknown} raw
 * @returns {{ ok: true, normalized: string, pathname: string, reason: null } | { ok: false, normalized: null, pathname: null, reason: string }}
 */
export function validateReturnUrl(raw) {
  if (typeof raw !== "string") return invalid("not_string");

  const trimmed = raw.trim();
  if (!trimmed) return invalid("empty");
  if (trimmed.length > MAX_RETURN_URL_LENGTH) return invalid("too_long");

  const trimmedRejection = validateRelativeUrlShape(trimmed);
  if (trimmedRejection) return trimmedRejection;

  let decoded;
  try {
    decoded = decodeForValidation(trimmed);
  } catch {
    return invalid("bad_encoding");
  }

  const decodedRejection = validateRelativeUrlShape(decoded, { decoded: true });
  if (decodedRejection) return decodedRejection;

  const originalParts = splitRelativeUrl(trimmed);
  const decodedParts = splitRelativeUrl(decoded);
  const pathname = normalizePathname(decodedParts.pathname);

  if (!pathname.startsWith("/") || pathname.startsWith("//")) {
    return invalid("invalid_pathname");
  }

  if (hasTraversalSegment(pathname)) return invalid("path_traversal");
  if (hasDeniedPrefix(pathname)) return invalid("denied_path");

  const sanitizedSearch = sanitizeSearch(originalParts.search);
  const normalized = `${pathname}${sanitizedSearch}${originalParts.hash}`;

  return {
    ok: true,
    normalized,
    pathname,
    reason: null,
  };
}

/**
 * Validate and persist an intended return URL for the current browser session.
 *
 * @param {unknown} raw
 * @returns {boolean}
 */
export function saveIntendedReturnUrl(raw) {
  const safe = validateReturnUrl(raw);
  if (!safe.ok) {
    // logReturnUrlFlow("saveIntendedReturnUrl:rejected", {
    //   raw,
    //   reason: safe.reason,
    // });
    return false;
  }

  const saved = writeSessionStorage(INTENDED_RETURN_URL_KEY, safe.normalized);
  // logReturnUrlFlow("saveIntendedReturnUrl:saved", {
  //   raw,
  //   normalized: safe.normalized,
  //   saved,
  // });
  return saved;
}

/**
 * @returns {string|null}
 */
export function peekIntendedReturnUrl() {
  return readSessionStorage(INTENDED_RETURN_URL_KEY);
}

export function clearIntendedReturnUrl() {
  // logReturnUrlFlow("clearIntendedReturnUrl", {
  //   previous: peekIntendedReturnUrl(),
  // });
  removeSessionStorage(INTENDED_RETURN_URL_KEY);
}

/**
 * Capture the current location as an intended return URL when it is safe.
 *
 * @returns {boolean}
 */
export function saveCurrentLocationAsIntendedReturnUrl() {
  try {
    if (!globalThis.window) return false;
    const { pathname, search, hash } = globalThis.window.location;
    const currentPath = `${pathname}${search}${hash}`;
    // logReturnUrlFlow("saveCurrentLocationAsIntendedReturnUrl", {
    //   currentPath,
    // });
    return saveIntendedReturnUrl(currentPath);
  } catch {
    return false;
  }
}

/**
 * Resolve the stored intended URL through URL safety and module/role policy.
 *
 * @param {Object|undefined} userData
 * @param {string|undefined} rawOverride
 * @param {{ allowPendingAuthorization?: boolean }} options
 * @returns {{ path: string, source: "default" | "invalid" | "denied" | "intended" | "pending_authorization" }}
 */
export function resolveAuthorizedReturnPath(
  userData,
  rawOverride,
  { allowPendingAuthorization = false } = {},
) {
  const fallback = getDefaultAuthenticatedPath(userData);
  const raw = rawOverride ?? peekIntendedReturnUrl();

  if (!raw) {
    const result = { path: fallback, source: "default" };
    // logReturnUrlFlow("resolveAuthorizedReturnPath:no-raw", {
    //   fallback,
    //   allowPendingAuthorization,
    //   role: userData?.role,
    //   modules: userData?.modules,
    //   result,
    // });
    return result;
  }

  const safe = validateReturnUrl(raw);
  if (!safe.ok) {
    const result = { path: fallback, source: "invalid" };
    // logReturnUrlFlow("resolveAuthorizedReturnPath:invalid", {
    //   raw,
    //   reason: safe.reason,
    //   fallback,
    //   allowPendingAuthorization,
    //   role: userData?.role,
    //   modules: userData?.modules,
    //   result,
    // });
    return result;
  }

  if (safe.pathname === ACCOUNT_MISCONFIGURED_PATH) {
    const result = isAccountMisconfigured(userData)
      ? { path: safe.normalized, source: "intended" }
      : { path: fallback, source: "denied" };
    // logReturnUrlFlow("resolveAuthorizedReturnPath:account-misconfigured", {
    //   raw,
    //   normalized: safe.normalized,
    //   fallback,
    //   allowPendingAuthorization,
    //   role: userData?.role,
    //   modules: userData?.modules,
    //   result,
    // });
    return result;
  }

  if (allowPendingAuthorization) {
    const result = { path: safe.normalized, source: "pending_authorization" };
    logReturnUrlFlow("resolveAuthorizedReturnPath:pending-authorization", {
      raw,
      normalized: safe.normalized,
      fallback,
      role: userData?.role,
      modules: userData?.modules,
      result,
    });
    return result;
  }

  if (canAccessPath(safe.pathname, userData)) {
    const result = { path: safe.normalized, source: "intended" };
    // logReturnUrlFlow("resolveAuthorizedReturnPath:allowed", {
    //   raw,
    //   normalized: safe.normalized,
    //   fallback,
    //   role: userData?.role,
    //   modules: userData?.modules,
    //   result,
    // });
    return result;
  }

  const result = { path: fallback, source: "denied" };
  // logReturnUrlFlow("resolveAuthorizedReturnPath:denied", {
  //   raw,
  //   normalized: safe.normalized,
  //   fallback,
  //   role: userData?.role,
  //   modules: userData?.modules,
  //   result,
  // });
  return result;
}

/**
 * Resolve the path used for post-login preload and chunk warmup when the user
 * is on `/`, `/login`, or already on an in-app route.
 *
 * On login/root, uses the stored return URL through {@link resolveAuthorizedReturnPath}
 * with `allowPendingAuthorization: true` (same as login navigation).
 *
 * @param {Object|undefined} userData
 * @param {string|undefined} currentPathname
 * @returns {string}
 */
export function getAuthenticatedEntryPath(userData, currentPathname) {
  const normalizedPathname = normalizePathname(currentPathname);

  if (normalizedPathname === "/" || normalizedPathname === "/login") {
    return resolveAuthorizedReturnPath(userData, undefined, {
      allowPendingAuthorization: true,
    }).path;
  }

  return normalizedPathname;
}
